import { Key } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AIMessageRole = "user" | "assistant" | "system";
export type AIFeedback = "positive" | "negative" | null;

export interface AIMessage {
  id: string;
  role: AIMessageRole;
  content: string;
  timestamp: number;
  feedback: AIFeedback;
  isLoading?: boolean;
}

export interface AIConversation {
  id: string;
  title: string;
  messages: AIMessage[];
  isPinned: boolean;
  isFavorite: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface AIUsageStats {
  todayPrompts: number;
  estimatedTokens: number;
  avgResponseMs: number;
  provider: string;
  model: string;
}

export interface AIContext {
  module: string;
  page: string;
  userRole: string;
  tenant: string;
  academicSession: string;
  breadcrumb: string;
}

// ─── Real Backend AI Integration ──────────────────────────────────────────────────────────

export async function callRealAI(
  prompt: string,
  context: AIContext,
  convId?: string
): Promise<{ content: string; provider?: string; model?: string; tokens?: number }> {
  const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const res = await fetch("/api/ai/chat/", {
      method: "POST",
      headers,
      body: JSON.stringify({
        message: prompt,
        conversation_id: convId && !convId.startsWith("conv-") ? convId : undefined,
        context,
      }),
    });

    if (res.ok) {
      const data = await res.json();
      return {
        content: data.content || data.response || "AI query processed successfully.",
        provider: data.provider_name || data.provider || "Ollama (Default)",
        model: data.model_name || data.model || "Llama 3 8B",
        tokens: data.total_tokens || Math.floor(prompt.length / 4) + 50,
      };
    }
  } catch (err) {
    console.warn("[Workspace AI] Backend API call failed, providing error response:", err);
  }

  throw new Error("Unable to reach AI completion endpoint.");
}

// ─── Storage Keys ─────────────────────────────────────────────────────────────

const CONV_KEY  = "college_erp_ai_conversations";
const USAGE_KEY = "college_erp_ai_usage";

// ─── Default Context ──────────────────────────────────────────────────────────

export const getDefaultContext = (): AIContext => ({
  module:          typeof window !== "undefined" ? window.location.pathname.split("/")[1] || "dashboard" : "dashboard",
  page:            typeof window !== "undefined" ? window.location.pathname : "/",
  userRole:        "administrator",
  tenant:          "Springfield Academic Cloud",
  academicSession: "2025–2026",
  breadcrumb:      "Workspace › AI Assistant",
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAIWorkspace() {
  const [conversations, setConversations] = useState<AIConversation[]>(() => {
    try { const r = localStorage.getItem(CONV_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
  });

  const [activeConvId, setActiveConvId] = useState<string | null>(
    () => conversations[0]?.id ?? null
  );

  const [usage, setUsage] = useState<AIUsageStats>(() => {
    try { const r = localStorage.getItem(USAGE_KEY); return r ? JSON.parse(r) : { todayPrompts: 0, estimatedTokens: 0, avgResponseMs: 0, provider: "Ollama (Local)", model: "llama3:latest" }; } catch {
      return { todayPrompts: 0, estimatedTokens: 0, avgResponseMs: 0, provider: "Ollama (Local)", model: "llama3:latest" };
    }
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const context = useRef<AIContext>(getDefaultContext());

  // Persist conversations
  useEffect(() => {
    try { localStorage.setItem(CONV_KEY, JSON.stringify(conversations)); } catch { /* silent */ }
  }, [conversations]);

  // Persist usage
  useEffect(() => {
    try { localStorage.setItem(USAGE_KEY, JSON.stringify(usage)); } catch { /* silent */ }
  }, [usage]);

  // Ctrl+Shift+A global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "A") {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const createConversation = useCallback((initialPrompt?: string): string => {
    const id = `conv-${Date.now()}`;
    const newConv: AIConversation = {
      id,
      title: initialPrompt ? initialPrompt.slice(0, 40) + "…" : "New Conversation",
      messages: [],
      isPinned: false,
      isFavorite: false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setConversations((prev) => [newConv, ...prev]);
    setActiveConvId(id);
    return id;
  }, []);

  const activeConversation = conversations.find((c) => c.id === activeConvId) ?? null;

  const sendMessage = useCallback(async (prompt: string, convId?: string) => {
    const targetId = convId ?? activeConvId ?? createConversation(prompt);

    const userMsg: AIMessage = { id: `msg-u-${Date.now()}`, role: "user", content: prompt, timestamp: Date.now(), feedback: null };
    const loadingMsg: AIMessage = { id: `msg-a-${Date.now()}`, role: "assistant", content: "", timestamp: Date.now(), feedback: null, isLoading: true };

    setConversations((prev) => prev.map((c) => c.id === targetId
      ? { ...c, messages: [...c.messages, userMsg, loadingMsg], updatedAt: Date.now(), title: c.messages.length === 0 ? prompt.slice(0, 40) + "…" : c.title }
      : c
    ));

    setIsLoading(true);
    const start = Date.now();

    try {
      const result = await callRealAI(prompt, context.current, targetId);
      const elapsed = Date.now() - start;

      setConversations((prev) => prev.map((c) => c.id === targetId
        ? { ...c, messages: c.messages.map((m) => m.id === loadingMsg.id ? { ...m, content: result.content, isLoading: false } : m) }
        : c
      ));

      setUsage((prev) => ({
        todayPrompts: prev.todayPrompts + 1,
        estimatedTokens: prev.estimatedTokens + (result.tokens || Math.floor(prompt.length / 4) + Math.floor(result.content.length / 4)),
        avgResponseMs: Math.round((prev.avgResponseMs * prev.todayPrompts + elapsed) / (prev.todayPrompts + 1)),
        provider: result.provider || prev.provider,
        model: result.model || prev.model,
      }));
    } catch {
      setConversations((prev) => prev.map((c) => c.id === targetId
        ? { ...c, messages: c.messages.map((m) => m.id === loadingMsg.id ? { ...m, content: "⚠️ AI service temporarily unavailable or uninitialized. Please check network/provider config.", isLoading: false } : m) }
        : c
      ));
    } finally {
      setIsLoading(false);
    }
  }, [activeConvId, createConversation]);

  const pinConversation = useCallback((id: string) => {
    setConversations((prev) => prev.map((c) => c.id === id ? { ...c, isPinned: !c.isPinned } : c));
  }, []);

  const favoriteConversation = useCallback((id: string) => {
    setConversations((prev) => prev.map((c) => c.id === id ? { ...c, isFavorite: !c.isFavorite } : c));
  }, []);

  const deleteConversation = useCallback((id: string) => {
    setConversations((prev) => {
      const remaining = prev.filter((c) => c.id !== id);
      if (activeConvId === id) setActiveConvId(remaining[0]?.id ?? null);
      return remaining;
    });
  }, [activeConvId]);

  const renameConversation = useCallback((id: string, title: string) => {
    setConversations((prev) => prev.map((c) => c.id === id ? { ...c, title } : c));
  }, []);

  const setFeedback = useCallback((convId: string, msgId: string, feedback: AIFeedback) => {
    setConversations((prev) => prev.map((c) => c.id === convId
      ? { ...c, messages: c.messages.map((m) => m.id === msgId ? { ...m, feedback } : m) }
      : c
    ));
  }, []);

  return {
    conversations,
    activeConversation,
    activeConvId,
    setActiveConvId,
    usage,
    isOpen,
    setIsOpen,
    isLoading,
    createConversation,
    sendMessage,
    pinConversation,
    favoriteConversation,
    deleteConversation,
    renameConversation,
    setFeedback,
    context: context.current,
  };
}
